import { Apps } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  Radio,
  RadioGroup,
} from '@mui/material';
import UserApiService from 'api/UserApiService';
import useApi from 'hooks/useApi';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { closeDialogAction } from 'store/actions/dialogsActions';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import User from 'types/entities/User';
import { ActionDescriptor } from 'types/enums/ActionDescriptor';

type ClientsDialogDeactivateUserProps = {
  activeUser: User;
  setClients: React.Dispatch<React.SetStateAction<User[]>>;
  onSuccess?: () => void;
};

const ClientsDialogDeactivateUser: React.FC<ClientsDialogDeactivateUserProps> = ({
  activeUser,
  setClients,
  onSuccess,
}) => {
  const [t] = useTranslation();
  const dialogs = useSelector((state) => state.dialogs);
  const [loading, setLoading] = useState(false);
  const [deactivationOption, setDeactivationOption] = useState<'scheduleAnonymize' | 'anonymizeData'>(
    'scheduleAnonymize'
  );
  const { dispatch } = useAppState();
  const { makeCall } = useApi();
  const appState = useSelector((state) => state.app);

  const close = () => {
    dispatch(closeDialogAction('clientDeactivateUser'));
  };

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogOption, setConfirmDialogOption] = useState<'scheduleAnonymize' | 'anonymizeData' | null>(null);

  const deactivateUser = async () => {
    setLoading(true);

    const requestData = {
      schedule_anonymize: deactivationOption === 'scheduleAnonymize',
      immediate_anonymize: deactivationOption === 'anonymizeData',
      cognito_username: activeUser.cognito_username,
      mail: activeUser.mail,
      action_id:
        deactivationOption === 'scheduleAnonymize'
          ? ActionDescriptor.ClientDeactivateAccountAndScheduleAnonymize
          : ActionDescriptor.ClientDeactivateAccountAndImmediateAnonymize,
      modified_by: appState.user?.id,
      tenant_slug: appState.customer?.slug,
    };

    const res = await makeCall(UserApiService.deactivateUser(activeUser.id, requestData), 'Error deactivating user');

    if (res.import === 'ok') {
      dispatch(setSnackbarAction({ message: 'User deactivated successfully', open: true, severity: 'success' }));
      dispatch(closeDialogAction('clientDeactivateUser'));
      onSuccess?.();
    } else {
      dispatch(
        setSnackbarAction({ message: 'Error! User is not deactivated. Try Again later', open: true, severity: 'error' })
      );
      dispatch(closeDialogAction('clientDeactivateUser'));
    }

    setLoading(false);
  };

  const handleDeactivateUser = () => {
    setConfirmDialogOption(deactivationOption);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAnonymize = async () => {
    setConfirmDialogOpen(false);
    await deactivateUser();
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setConfirmDialogOption(null);
  };

  return (
    <>
    <Dialog style={{ maxHeight: 435 }} maxWidth="sm" open={dialogs.clientDeactivateUser || false}>
        <DialogTitle>{t('Deactivate_User')}</DialogTitle>
      <DialogContent>
          <RadioGroup
            value={deactivationOption}
            onChange={(event) => setDeactivationOption(event.target.value as 'scheduleAnonymize' | 'anonymizeData')}
          >
          <FormControlLabel
            value="scheduleAnonymize"
            control={<Radio color="primary" />}
              title={t('Schedule_Anonymize_Tooltip')}
            label="Deactivate Account & Schedule Anonymization"
          />
          <FormControlLabel
            value="anonymizeData"
            control={<Radio color="primary" />}
            label="Deactivate Account & Anonymize GDPR Data Now"
              title={t('Anonymize_Tooltip')}
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={close}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" loading={loading} onClick={handleDeactivateUser} color="primary">
            {t('Submit')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        maxWidth="xs"
        fullWidth
        scroll="paper"
        sx={{ '& .MuiDialog-paper': { overflow: 'visible' } }}
      >
        <DialogTitle>
          {confirmDialogOption === 'scheduleAnonymize'
            ? t('Confirm_Schedule_Anonymization')
            : t('Confirm_Data_Anonymization')}
        </DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <Typography>
            {confirmDialogOption === 'scheduleAnonymize'
              ? t('Schedule_Anonymize_Confirm_Text')
              : t('Confirm_Anonymize_Data_Now')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={handleCloseConfirmDialog}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleConfirmAnonymize}>
            {t('Submit')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ClientsDialogDeactivateUser;
